/**
 * The planet mesh's layout for one grid size: where each tile's vertices start, the triangles
 * of every block (a flat top, a rounded edge in a few steps, a wall) and each corner's directions.
 * It depends on the grid alone, so it is built once per size and kept.
 */
import type { Grid } from '@bramvasterd/terrella-rules';

const BW = 0.15;
/** How far the rounded edge droops below the top: where the wall begins. */
export const RB = 0.05;
/** Steps in a tile's rounded edge. */
export const SEG = 3;

/** The rounded edge, step by step: how far out (`s`), how far down (`h`), and its normal's mix. */
export const PROFILE = Array.from({ length: SEG + 1 }, (_, r) => {
  const p = ((r / SEG) * Math.PI) / 2;
  return {
    s: 1 - BW + BW * Math.sin(p),
    h: -RB * (1 - Math.cos(p)),
    cs: Math.cos(p),
    sn: Math.sin(p),
  };
});

export interface Layout {
  count: number;
  /** Vertex offset per tile, and one past the last tile. */
  off: Int32Array;
  /** Corner offset per tile. */
  coff: Int32Array;
  index: Uint32Array;
  /** Corner directions (unit) and the outward direction at each corner, 3 floats each. */
  cor: Float32Array;
  out: Float32Array;
}

const layouts = new Map<number, Layout>();

/** The layout for a grid size, built once and kept. */
export function layoutFor(grid: Grid): Layout {
  const cached = layouts.get(grid.subdivisions);
  if (cached) return cached;
  const n = grid.count;
  const off = new Int32Array(n + 1);
  const coff = new Int32Array(n + 1);
  for (let i = 0; i < n; i++) {
    const nc = (grid.corners[i]?.length ?? 0) / 3;
    off[i + 1] = (off[i] ?? 0) + 1 + (SEG + 2) * nc;
    coff[i + 1] = (coff[i] ?? 0) + nc;
  }
  const index: number[] = [];
  for (let i = 0; i < n; i++) {
    const nc = (grid.corners[i]?.length ?? 0) / 3;
    const o = off[i] ?? 0;
    const ring = (r: number): number => o + 1 + r * nc;
    for (let k = 0; k < nc; k++) index.push(o, ring(0) + k, ring(0) + ((k + 1) % nc));
    for (let r = 0; r <= SEG; r++) {
      for (let k = 0; k < nc; k++) {
        const k2 = (k + 1) % nc;
        const a = ring(r) + k;
        const b = ring(r) + k2;
        const c = ring(r + 1) + k;
        const d = ring(r + 1) + k2;
        index.push(a, c, b, b, c, d);
      }
    }
  }
  const total = coff[n] ?? 0;
  const cor = new Float32Array(total * 3);
  const out = new Float32Array(total * 3);
  for (let i = 0; i < n; i++) {
    const cx = grid.centers[i * 3] ?? 0;
    const cy = grid.centers[i * 3 + 1] ?? 0;
    const cz = grid.centers[i * 3 + 2] ?? 0;
    const corners = grid.corners[i];
    if (!corners) continue;
    const nc = corners.length / 3;
    for (let k = 0; k < nc; k++) {
      const qx = corners[k * 3] ?? 0;
      const qy = corners[k * 3 + 1] ?? 0;
      const qz = corners[k * 3 + 2] ?? 0;
      const j = ((coff[i] ?? 0) + k) * 3;
      const d = qx * cx + qy * cy + qz * cz;
      const ox = qx - cx * d;
      const oy = qy - cy * d;
      const oz = qz - cz * d;
      const l = Math.hypot(ox, oy, oz) || 1;
      cor[j] = qx;
      cor[j + 1] = qy;
      cor[j + 2] = qz;
      out[j] = ox / l;
      out[j + 1] = oy / l;
      out[j + 2] = oz / l;
    }
  }
  const layout: Layout = { count: n, off, coff, index: new Uint32Array(index), cor, out };
  layouts.set(grid.subdivisions, layout);
  return layout;
}
